export function normalizeLawyerText(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

export function normalizeLawyerDocument(value) {
  return (value || '').replace(/\D/g, '');
}


export function normalizePhoneValue(value) {
  const digits = (value || '').replace(/\D/g, '');
  const withoutCountryCode = digits.length > 11 && digits.startsWith('55')
    ? digits.slice(2)
    : digits;
  return withoutCountryCode.slice(0, 11);
}

export function formatPhoneValue(value) {
  const digits = normalizePhoneValue(value);

  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

export function normalizeDocumentValue(value) {
  return (value || '').replace(/\D/g, '').slice(0, 14);
}

export function formatDocumentValue(value) {
  const digits = normalizeDocumentValue(value);

  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  if (digits.length <= 11) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

export function normalizeOabValue(value) {
  const cleaned = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  let state = '';
  let rest = cleaned;

  if (/^[A-Z]{1,2}/.test(cleaned)) {
    state = cleaned.match(/^[A-Z]{1,2}/)?.[0] || '';
    rest = cleaned.slice(state.length);
  }

  const digits = rest.replace(/\D/g, '').slice(0, 10);
  const suffix = rest.replace(/[^A-Z]/g, '').slice(0, 1);

  return `${state}${digits}${suffix}`;
}

export function formatOabValue(value) {
  const normalized = normalizeOabValue(value);
  if (!normalized) return '';

  const state = normalized.match(/^[A-Z]{1,2}/)?.[0] || '';
  const rest = normalized.slice(state.length);
  const digits = rest.replace(/\D/g, '');
  const suffix = rest.replace(/[^A-Z]/g, '').slice(0, 1);

  const parts = [];
  if (state) parts.push(state);
  if (digits) parts.push(digits);

  const base = parts.join(' ');
  return suffix ? `${base}-${suffix}` : base;
}

export function buildSearchableLawyerText(parts = []) {
  return normalizeLawyerText(parts.filter(Boolean).join(' '));
}

function getCandidateName(lawyer) {
  return lawyer?.nome || lawyer?.name || '';
}

function getCandidateOab(lawyer) {
  return (lawyer?.oab || '').toUpperCase().trim();
}

function getCandidateDocument(lawyer) {
  const direct = lawyer?.document || lawyer?.documento || '';
  const fromArray = Array.isArray(lawyer?.documentos) ? lawyer.documentos[0] : '';
  return normalizeLawyerDocument(direct || fromArray);
}

export function extractActiveLawyerCandidates(parties = {}) {
  const activePoles = Array.isArray(parties?.activePoles) ? parties.activePoles : [];

  return activePoles
    .filter((pole) => (pole?.type || '').toUpperCase() === 'ADVOGADO' && (pole?.name || pole?.nome))
    .map((pole) => ({
      nome: pole.name || pole.nome || '',
      oab: pole.oab || '',
      document: pole.document || pole.documento || '',
      documentos: pole.document || pole.documento ? [pole.document || pole.documento] : [],
      type: pole.type,
      pole: pole.pole,
    }));
}

export function filterDisplayPoles(parties = {}) {
  const sanitize = (items = []) => items.filter((pole) => (pole?.type || '').toUpperCase() !== 'ADVOGADO');

  return {
    activePoles: sanitize(parties?.activePoles || []),
    passivePoles: sanitize(parties?.passivePoles || []),
  };
}

export function findMatchingLawyerPartners({ partners = [], content = '', lawyers = [] }) {
  if (!partners?.length) return [];

  const searchableText = buildSearchableLawyerText([
    content,
    ...lawyers.flatMap((lawyer) => [getCandidateName(lawyer), getCandidateOab(lawyer), getCandidateDocument(lawyer)]),
  ]);

  const lawyerNames = lawyers
    .map((lawyer) => normalizeLawyerText(getCandidateName(lawyer)))
    .filter(Boolean);
  const lawyerOabs = lawyers
    .map((lawyer) => getCandidateOab(lawyer))
    .filter(Boolean);
  const lawyerDocuments = lawyers
    .map((lawyer) => getCandidateDocument(lawyer))
    .filter(Boolean);

  return partners
    .filter((partner) => {
      const normalizedPartnerName = normalizeLawyerText(partner.nome);
      const normalizedPartnerOab = (partner.oab || '').toUpperCase().trim();
      const normalizedPartnerDocument = normalizeLawyerDocument(partner.documento);

      const matchesByName = normalizedPartnerName && (
        searchableText.includes(normalizedPartnerName) ||
        lawyerNames.includes(normalizedPartnerName)
      );

      const matchesByOab = normalizedPartnerOab && (
        searchableText.includes(normalizedPartnerOab) ||
        lawyerOabs.includes(normalizedPartnerOab)
      );

      const matchesByDocument = normalizedPartnerDocument && (
        searchableText.includes(normalizedPartnerDocument) ||
        lawyerDocuments.includes(normalizedPartnerDocument)
      );

      return matchesByName || matchesByOab || matchesByDocument;
    })
    .sort((a, b) => {
      if (b.rating !== a.rating) return b.rating - a.rating;
      return a.nome.localeCompare(b.nome, 'pt-BR');
    });
}

export function getLawyerPartnerAppearance(classificacao) {
  switch ((classificacao || '').toUpperCase()) {
    case 'BOM':
      return {
        badge: 'bg-green-100 text-green-800 border-green-200',
        panel: 'bg-green-50 border-green-200 text-green-900',
        label: 'Parceiro bom',
      };
    case 'RUIM':
      return {
        badge: 'bg-red-100 text-red-800 border-red-200',
        panel: 'bg-red-50 border-red-200 text-red-900',
        label: 'Parceiro ruim',
        contactBlocked: true,
      };
    default:
      return {
        badge: 'bg-orange-100 text-orange-800 border-orange-200',
        panel: 'bg-orange-50 border-orange-200 text-orange-900',
        label: 'Parceiro neutro',
      };
  }
}
